import { getAccessToken } from '@/lib/tokenStorage';

import { enhancedApi } from '../api/baseApi';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export type SchedulerReferenceUser = {
  equipmentId: string;
  systemUserId?: string | null;
  name: string;
  email?: string | null;
  siteId?: string | null;
  siteName?: string | null;
  isPse: boolean;
  isPsv: boolean;
  isTechnician: boolean;
};

export type SchedulerCalendarTrip = {
  tripId: string;
  shipmentNumber: string;
  startUtc?: string | null;
  endUtc?: string | null;
  recurring: boolean;
  frequency?: string | null;
  psEmbryologistId?: string | null;
  psInternId?: string | null;
};

export type SchedulerTripStop = {
  stopId?: string;
  region?: string | null;
  siteCode?: string | null;
  startUtc?: string | null;
  endUtc?: string | null;
  psVetId?: string | null;
  accountNumber?: string;
  description?: string;
  comments?: string;
  enteredBy?: string;
  status: 'tentative' | 'confirmed' | 'scheduled';
  opu?: number | null;
  ftTx?: number | null;
  fzTx?: number | null;
  dfrs?: number | null;
  pregs?: number | null;
  flush?: number | null;
};

export type SchedulerTripDetail = SchedulerCalendarTrip & {
  stops: SchedulerTripStop[];
};

export type SchedulerCalendarEvent = {
  eventId: string;
  type: string;
  psVetId?: string | null;
  startUtc?: string | null;
  endUtc?: string | null;
  coveringSiteCode?: string | null;
  description?: string | null;
  enteredBy?: string | null;
  psEmbryologistId?: string | null;
  attendees: string[];
};

export type SchedulerUserSetting = {
  userId: string;
  calendarName?: string | null;
  region?: string | null;
  siteCode?: string | null;
  colorHex?: string | null;
  visible: boolean;
};

export type UpsertSchedulerUserSettingInput = {
  userId: string;
  calendarName?: string | null;
  region?: string | null;
  siteCode?: string | null;
  colorHex?: string | null;
  visible: boolean;
};

export type SchedulerGroup = {
  groupId: string;
  name: string;
  userIds: string[];
};

export type UpsertSchedulerGroupInput = {
  name: string;
  userIds: string[];
};

export type SchedulerRecurringRule = {
  ruleId: string;
  name: string;
  frequency: string;
  startUtc: string;
  endUtc?: string | null;
  active: boolean;
};

export type UpsertSchedulerRecurringRuleInput = {
  name: string;
  frequency: string;
  startUtc: string;
  endUtc?: string | null;
  active: boolean;
};

export type CreateSchedulerTripInput = {
  recurring: boolean;
  frequency: string;
  shipmentNumber: string;
  psEmbryologistId?: string | null;
  psInternId?: string | null;
  stops: {
    stopId?: string;
    region?: string | null;
    siteCode?: string | null;
    startUtc?: string | null;
    endUtc?: string | null;
    psVetId?: string | null;
    accountNumber?: string;
    description?: string;
    comments?: string;
    enteredBy?: string;
    status: 'tentative' | 'confirmed' | 'scheduled';
    opu?: number | null;
    ftTx?: number | null;
    fzTx?: number | null;
    dfrs?: number | null;
    pregs?: number | null;
    flush?: number | null;
  }[];
};

export type UpdateSchedulerTripInput = CreateSchedulerTripInput;

export type CreateSchedulerEventInput = {
  type: string;
  psVetId?: string | null;
  startUtc?: string | null;
  endUtc?: string | null;
  coveringSiteCode?: string | null;
  description?: string;
  enteredBy?: string;
  psEmbryologistId?: string | null;
  attendees: string[];
};

export type UpdateSchedulerEventInput = CreateSchedulerEventInput;

export type PaginationResult<T> = {
  items: T[];
  total: number;
};

export type SchedulerReferenceUserQuery = {
  role?: 'pse' | 'psv' | 'tech';
  siteId?: string;
  searchTerm?: string;
  activeOnly?: boolean;
  startRow?: number;
  endRow?: number;
};

export type SchedulerCalendarQuery = {
  startUtc: string;
  endUtc: string;
  siteCode?: string;
  region?: string;
  searchTerm?: string;
  startRow?: number;
  endRow?: number;
};

const mapPagination = <T>(
  raw: Record<string, unknown>,
): PaginationResult<T> => ({
  items: (raw?.items ?? raw?.Items ?? []) as T[],
  total: (raw?.total ?? raw?.Total ?? 0) as number,
});

const buildQueryString = (
  params: Record<string, string | number | boolean>,
) => {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    query.set(key, String(value));
  }
  return query.toString();
};

async function fetchSchedulerPagination<T>(
  path: string,
  queryArg: Record<string, string | number | boolean>,
): Promise<{
  data?: PaginationResult<T>;
  error?: { status: number | string; data: string };
}> {
  try {
    const token = getAccessToken();
    const queryString = buildQueryString(queryArg);
    const response = await fetch(`${API_BASE_URL}${path}?${queryString}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.text();
      return { error: { status: response.status, data: error } };
    }

    const data = await response.json();
    return { data: mapPagination<T>(data) };
  } catch (error: unknown) {
    return {
      error: {
        status: 'FETCH_ERROR',
        data: error instanceof Error ? error.message : 'Unknown error',
      },
    };
  }
}

async function fetchSchedulerItem<T>(
  path: string,
): Promise<{ data?: T; error?: { status: number | string; data: string } }> {
  try {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.text();
      return { error: { status: response.status, data: error } };
    }

    const data = (await response.json()) as T;
    return { data };
  } catch (error: unknown) {
    return {
      error: {
        status: 'FETCH_ERROR',
        data: error instanceof Error ? error.message : 'Unknown error',
      },
    };
  }
}

async function sendSchedulerMutation<T>(
  path: string,
  method: 'POST' | 'PUT' | 'DELETE',
  body?: unknown,
): Promise<{ data?: T; error?: { status: number | string; data: string } }> {
  try {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
        ...(method !== 'DELETE' ? { 'Content-Type': 'application/json' } : {}),
      },
      credentials: 'include',
      body: method !== 'DELETE' ? JSON.stringify(body ?? {}) : undefined,
    });

    if (!response.ok) {
      const error = await response.text();
      return { error: { status: response.status, data: error } };
    }

    if (response.status === 204) {
      return { data: undefined as T };
    }

    const data = (await response.json()) as T;
    return { data };
  } catch (error: unknown) {
    return {
      error: {
        status: 'FETCH_ERROR',
        data: error instanceof Error ? error.message : 'Unknown error',
      },
    };
  }
}

export const serviceSchedulerApi = enhancedApi.injectEndpoints({
  endpoints: (build) => ({
    getSchedulerReferenceUsers: build.query<
      PaginationResult<SchedulerReferenceUser>,
      SchedulerReferenceUserQuery | void
    >({
      queryFn: async (arg) => {
        const queryArg = arg ?? {};
        return fetchSchedulerPagination<SchedulerReferenceUser>(
          '/api/service-scheduler/reference-users',
          {
            ...(queryArg.role ? { role: queryArg.role } : {}),
            ...(queryArg.siteId ? { siteId: queryArg.siteId } : {}),
            ...(queryArg.searchTerm ? { searchTerm: queryArg.searchTerm } : {}),
            activeOnly: queryArg.activeOnly ?? true,
            startRow: queryArg.startRow ?? 1,
            endRow: queryArg.endRow ?? 200,
          },
        );
      },
      providesTags: ['Lookups'],
    }),
    getSchedulerCalendarTrips: build.query<
      PaginationResult<SchedulerCalendarTrip>,
      SchedulerCalendarQuery
    >({
      queryFn: async (queryArg) => {
        return fetchSchedulerPagination<SchedulerCalendarTrip>(
          '/api/service-scheduler/calendar/trips',
          {
            startUtc: queryArg.startUtc,
            endUtc: queryArg.endUtc,
            ...(queryArg.siteCode ? { siteCode: queryArg.siteCode } : {}),
            ...(queryArg.region ? { region: queryArg.region } : {}),
            ...(queryArg.searchTerm ? { searchTerm: queryArg.searchTerm } : {}),
            startRow: queryArg.startRow ?? 1,
            endRow: queryArg.endRow ?? 500,
          },
        );
      },
      providesTags: ['Lookups'],
    }),
    getSchedulerTripById: build.query<SchedulerTripDetail, string>({
      queryFn: (tripId) =>
        fetchSchedulerItem<SchedulerTripDetail>(
          `/api/service-scheduler/trips/${tripId}`,
        ),
      providesTags: ['Lookups'],
    }),
    getSchedulerCalendarEvents: build.query<
      PaginationResult<SchedulerCalendarEvent>,
      SchedulerCalendarQuery
    >({
      queryFn: async (queryArg) => {
        return fetchSchedulerPagination<SchedulerCalendarEvent>(
          '/api/service-scheduler/calendar/events',
          {
            startUtc: queryArg.startUtc,
            endUtc: queryArg.endUtc,
            ...(queryArg.siteCode ? { siteCode: queryArg.siteCode } : {}),
            ...(queryArg.region ? { region: queryArg.region } : {}),
            ...(queryArg.searchTerm ? { searchTerm: queryArg.searchTerm } : {}),
            startRow: queryArg.startRow ?? 1,
            endRow: queryArg.endRow ?? 500,
          },
        );
      },
      providesTags: ['Lookups'],
    }),
    getSchedulerEventById: build.query<SchedulerCalendarEvent, string>({
      queryFn: (eventId) =>
        fetchSchedulerItem<SchedulerCalendarEvent>(
          `/api/service-scheduler/events/${eventId}`,
        ),
      providesTags: ['Lookups'],
    }),
    createSchedulerTrip: build.mutation<
      SchedulerCalendarTrip,
      CreateSchedulerTripInput
    >({
      queryFn: (input) =>
        sendSchedulerMutation<SchedulerCalendarTrip>(
          '/api/service-scheduler/trips',
          'POST',
          input,
        ),
      invalidatesTags: ['Lookups'],
    }),
    updateSchedulerTrip: build.mutation<
      SchedulerCalendarTrip,
      { tripId: string; input: UpdateSchedulerTripInput }
    >({
      queryFn: ({ tripId, input }) =>
        sendSchedulerMutation<SchedulerCalendarTrip>(
          `/api/service-scheduler/trips/${tripId}`,
          'PUT',
          input,
        ),
      invalidatesTags: ['Lookups'],
    }),
    deleteSchedulerTrip: build.mutation<void, { tripId: string }>({
      queryFn: ({ tripId }) =>
        sendSchedulerMutation<void>(
          `/api/service-scheduler/trips/${tripId}`,
          'DELETE',
        ),
      invalidatesTags: ['Lookups'],
    }),
    createSchedulerEvent: build.mutation<
      SchedulerCalendarEvent,
      CreateSchedulerEventInput
    >({
      queryFn: (input) =>
        sendSchedulerMutation<SchedulerCalendarEvent>(
          '/api/service-scheduler/events',
          'POST',
          input,
        ),
      invalidatesTags: ['Lookups'],
    }),
    updateSchedulerEvent: build.mutation<
      SchedulerCalendarEvent,
      { eventId: string; input: UpdateSchedulerEventInput }
    >({
      queryFn: ({ eventId, input }) =>
        sendSchedulerMutation<SchedulerCalendarEvent>(
          `/api/service-scheduler/events/${eventId}`,
          'PUT',
          input,
        ),
      invalidatesTags: ['Lookups'],
    }),
    deleteSchedulerEvent: build.mutation<void, { eventId: string }>({
      queryFn: ({ eventId }) =>
        sendSchedulerMutation<void>(
          `/api/service-scheduler/events/${eventId}`,
          'DELETE',
        ),
      invalidatesTags: ['Lookups'],
    }),
    getSchedulerUserSettings: build.query<SchedulerUserSetting[], void>({
      queryFn: () =>
        fetchSchedulerItem<SchedulerUserSetting[]>(
          '/api/service-scheduler/settings/users',
        ),
      providesTags: ['Lookups'],
    }),
    upsertSchedulerUserSetting: build.mutation<
      SchedulerUserSetting,
      UpsertSchedulerUserSettingInput
    >({
      queryFn: (input) =>
        sendSchedulerMutation<SchedulerUserSetting>(
          '/api/service-scheduler/settings/users',
          'PUT',
          input,
        ),
      invalidatesTags: ['Lookups'],
    }),
    getSchedulerGroups: build.query<SchedulerGroup[], void>({
      queryFn: () =>
        fetchSchedulerItem<SchedulerGroup[]>(
          '/api/service-scheduler/settings/groups',
        ),
      providesTags: ['Lookups'],
    }),
    createSchedulerGroup: build.mutation<
      SchedulerGroup,
      UpsertSchedulerGroupInput
    >({
      queryFn: (input) =>
        sendSchedulerMutation<SchedulerGroup>(
          '/api/service-scheduler/settings/groups',
          'POST',
          input,
        ),
      invalidatesTags: ['Lookups'],
    }),
    updateSchedulerGroup: build.mutation<
      SchedulerGroup,
      { groupId: string; input: UpsertSchedulerGroupInput }
    >({
      queryFn: ({ groupId, input }) =>
        sendSchedulerMutation<SchedulerGroup>(
          `/api/service-scheduler/settings/groups/${groupId}`,
          'PUT',
          input,
        ),
      invalidatesTags: ['Lookups'],
    }),
    deleteSchedulerGroup: build.mutation<void, { groupId: string }>({
      queryFn: ({ groupId }) =>
        sendSchedulerMutation<void>(
          `/api/service-scheduler/settings/groups/${groupId}`,
          'DELETE',
        ),
      invalidatesTags: ['Lookups'],
    }),
    getSchedulerRecurringRules: build.query<SchedulerRecurringRule[], void>({
      queryFn: () =>
        fetchSchedulerItem<SchedulerRecurringRule[]>(
          '/api/service-scheduler/settings/recurring',
        ),
      providesTags: ['Lookups'],
    }),
    createSchedulerRecurringRule: build.mutation<
      SchedulerRecurringRule,
      UpsertSchedulerRecurringRuleInput
    >({
      queryFn: (input) =>
        sendSchedulerMutation<SchedulerRecurringRule>(
          '/api/service-scheduler/settings/recurring',
          'POST',
          input,
        ),
      invalidatesTags: ['Lookups'],
    }),
    updateSchedulerRecurringRule: build.mutation<
      SchedulerRecurringRule,
      { ruleId: string; input: UpsertSchedulerRecurringRuleInput }
    >({
      queryFn: ({ ruleId, input }) =>
        sendSchedulerMutation<SchedulerRecurringRule>(
          `/api/service-scheduler/settings/recurring/${ruleId}`,
          'PUT',
          input,
        ),
      invalidatesTags: ['Lookups'],
    }),
    deleteSchedulerRecurringRule: build.mutation<void, { ruleId: string }>({
      queryFn: ({ ruleId }) =>
        sendSchedulerMutation<void>(
          `/api/service-scheduler/settings/recurring/${ruleId}`,
          'DELETE',
        ),
      invalidatesTags: ['Lookups'],
    }),
  }),
});

export const {
  useGetSchedulerReferenceUsersQuery,
  useGetSchedulerCalendarTripsQuery,
  useGetSchedulerTripByIdQuery,
  useGetSchedulerCalendarEventsQuery,
  useGetSchedulerEventByIdQuery,
  useCreateSchedulerTripMutation,
  useUpdateSchedulerTripMutation,
  useDeleteSchedulerTripMutation,
  useCreateSchedulerEventMutation,
  useUpdateSchedulerEventMutation,
  useDeleteSchedulerEventMutation,
  useGetSchedulerUserSettingsQuery,
  useUpsertSchedulerUserSettingMutation,
  useGetSchedulerGroupsQuery,
  useCreateSchedulerGroupMutation,
  useUpdateSchedulerGroupMutation,
  useDeleteSchedulerGroupMutation,
  useGetSchedulerRecurringRulesQuery,
  useCreateSchedulerRecurringRuleMutation,
  useUpdateSchedulerRecurringRuleMutation,
  useDeleteSchedulerRecurringRuleMutation,
} = serviceSchedulerApi;

export function useSchedulerReferenceUsers(
  query?: SchedulerReferenceUserQuery,
) {
  const response = useGetSchedulerReferenceUsersQuery(query);
  return {
    ...response,
    users: response.data?.items ?? [],
    total: response.data?.total ?? 0,
  };
}

export function useSchedulerCalendarData(query: SchedulerCalendarQuery) {
  const tripsResponse = useGetSchedulerCalendarTripsQuery(query);
  const eventsResponse = useGetSchedulerCalendarEventsQuery(query);

  return {
    trips: tripsResponse.data?.items ?? [],
    events: eventsResponse.data?.items ?? [],
    totalTrips: tripsResponse.data?.total ?? 0,
    totalEvents: eventsResponse.data?.total ?? 0,
    isLoading: tripsResponse.isLoading || eventsResponse.isLoading,
    isFetching: tripsResponse.isFetching || eventsResponse.isFetching,
    error: tripsResponse.error ?? eventsResponse.error,
  };
}

export const REGION_LABELS_BY_ID: Record<number, string> = {
  1: 'Plains',
  2: 'West',
  3: 'South',
};

export const REGION_KEYS_BY_NAME: Record<string, string[]> = {
  plains: ['plains', 'planes', '1'],
  west: ['west', '2'],
  south: ['south', '3'],
};

export type SchedulerDropdownOption = {
  label: string;
  value: string;
};

export function useSchedulerReferenceUserOptions(
  query?: SchedulerReferenceUserQuery,
) {
  const { users, ...rest } = useSchedulerReferenceUsers(query);
  const options: SchedulerDropdownOption[] = users.map((u) => ({
    label: u.name,
    value: u.equipmentId,
  }));

  return {
    ...rest,
    users,
    options,
  };
}
